import { NgClass } from '@angular/common';
import { Component, computed, effect, input, signal } from '@angular/core';

@Component({
  selector: 'ui-user-avatar',
  standalone: true,
  imports: [NgClass],
  templateUrl: './user-avatar.html',
  host: {
    class: 'inline-flex shrink-0',
  },
})
export class UserAvatar {
  readonly name = input.required<string>();
  /** URL da foto (`User.avatarUrl` / `avatar_url` da API). Vazio mostra as iniciais. */
  readonly src = input<string | null | undefined>(null);
  readonly size = input<'xs' | 'sm' | 'md' | 'lg'>('sm');
  /**
   * `circle` - avatar redondo (listas, header).
   * `portrait` - retangular 3:4 estilo foto 3x4 (detalhes).
   */
  readonly shape = input<'circle' | 'portrait'>('circle');

  protected readonly imageFailed = signal(false);

  protected readonly photoUrl = computed(() => {
    const value = this.src()?.trim();
    return value ? value : null;
  });

  protected readonly initials = computed(() => initialsFromName(this.name()));

  protected readonly frameClass = computed(() => {
    const portrait = this.shape() === 'portrait';
    const radius = portrait ? 'rounded-md' : 'rounded-full';
    const base =
      'inline-flex items-center justify-center overflow-hidden bg-[var(--color-primary-soft)] font-semibold text-[var(--color-primary)]';

    if (portrait) {
      switch (this.size()) {
        case 'lg':
          return `${base} ${radius} aspect-[3/4] h-28 w-auto text-2xl`;
        case 'md':
          return `${base} ${radius} aspect-[3/4] h-16 w-auto text-sm`;
        case 'xs':
          return `${base} ${radius} aspect-[3/4] h-9 w-auto text-[0.65rem]`;
        default:
          return `${base} ${radius} aspect-[3/4] h-12 w-auto text-xs`;
      }
    }

    switch (this.size()) {
      case 'lg':
        return `${base} ${radius} size-20 text-2xl`;
      case 'md':
        return `${base} ${radius} size-10 text-sm`;
      case 'xs':
        return `${base} ${radius} size-7 text-[0.65rem]`;
      default:
        return `${base} ${radius} size-8 text-xs`;
    }
  });

  constructor() {
    effect(() => {
      this.photoUrl();
      this.imageFailed.set(false);
    });
  }

  protected onImageError(): void {
    this.imageFailed.set(true);
  }
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}
